/* eslint @typescript-eslint/prefer-nullish-coalescing: 1 -- Do not want to break existing code by switching to nullish coalesce blidnly */
import { gql, request } from 'graphql-request';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

import logToDatadog from '@/logger/logToDatadog';

import { getGraphQlEndpoint } from '../api/backendAPI';
import queryClient from '../api/queryClient';
import type {
  Countries,
  Countries_content,
  Countries_content_countries,
} from './types/Countries';

export type CountryDetails = Countries_content_countries;

export const query = gql`
  query Countries($countryCode: String) {
    content {
      countries(countryCode: $countryCode) {
        currency
        currencyForSubscription
        supportedCurrencies
        status
        membershipStatus
        countryCode
        shippingRate {
          description
          price
        }
        eu
        name
        regions {
          label
          value
          labelMsgKey
        }
        merchant {
          prod
          staging
        }
        disallowedProductSkus
        labelMsgKey
        enableForRma
        showNoAdditionalTaxesMessage
      }
    }
  }
`;

function withQueryKey(vars: { countryCode?: string } = {}) {
  return {
    key: ['Countries', { vars }],
    fetch: async () => {
      const url = getGraphQlEndpoint()!;

      const data: Countries = await request(url, query, vars);

      const byCountryCode = new Map();

      const countries = data.content?.countries;

      for (const country of countries || []) {
        byCountryCode.set(country?.countryCode, country);
      }

      return { countries, byCountryCode };
    },
  };
}

export function useSellToCountriesList({
  isMembership,
  showAll,
}: {
  isMembership?: boolean;
  showAll?: boolean;
}): {
  isLoading: boolean;
  data: Countries_content_countries[] | undefined;
} {
  const { key, fetch } = withQueryKey();
  const { data, isLoading } = useQuery(key, fetch);

  const countries = data?.countries;

  const filterFn = ({
    status,
    membershipStatus,
  }: Countries_content_countries) => {
    if (showAll) return true;
    if (isMembership) return membershipStatus ?? status;
    return status;
  };

  return {
    isLoading,
    data: countries ? countries.filter(filterFn as any) : undefined,
  };
}

export async function getSellToCountry(
  countryCode: string,
): Promise<Countries_content_countries> {
  const { key, fetch } = withQueryKey({ countryCode });
  const { byCountryCode } = await queryClient.fetchQuery(key, fetch);

  return byCountryCode.get(countryCode);
}

export function useCountryDetails(countryCode: string) {
  const { key, fetch } = withQueryKey({ countryCode });
  const { data, isLoading, isError } = useQuery(key, fetch);

  return {
    isLoading,
    isError,
    data: data?.byCountryCode.get(countryCode),
  };
}

export function useFullCountriesList() {
  const { key, fetch } = withQueryKey();
  const { data, isLoading, isError } = useQuery(key, fetch);

  return {
    isLoading,
    isError,
    data: data?.countries,
  };
}

export function useSellToCountry(countryCode: string) {
  const { key, fetch } = withQueryKey({ countryCode });

  const queryResult: UseQueryResult<Countries_content> = useQuery({
    queryKey: key,
    queryFn: fetch,
    onError: (error) => {
      void logToDatadog('countries', 'Error fetching Countries', {
        error,
      });
    },
  });
  return queryResult;
}
